import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableColumn,
	ContentTableItem
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useActiveBanners from "src/features/vendorSponsorship/hooks/useActiveBanners/useActiveBanners";
import useBannerLibrary from "src/features/vendorSponsorship/hooks/useBannerLibary/useBannerLibrary";
import useMakeBannersInactive from "src/features/vendorSponsorship/hooks/useMakeBannersInactive/useMakeBannerInactive";
import useMoveCarouselBanner from "src/features/vendorSponsorship/hooks/useMoveCarouselBanner/useMoveCarouselBanner";
import useMakeBannersActive from "src/features/vendorSponsorship/hooks/useMakeBannersActive/useMakeBannerActive";
import useDeleteCarouselBanners from "src/features/vendorSponsorship/hooks/useDeleteCarouselBanners/useDeleteCarouselBanners";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";
import AudienceCell from "src/components/AudienceCell/AudienceCell";

interface CarouselTableItem extends ContentTableItem {
	data: {
		audienceGroups: { id: number; name: string }[];
	};
}

function mapBannersToContentTableItems(
	activeBanners: Banner[],
	libraryBanners: Banner[]
): CarouselTableItem[] {
	const live = activeBanners.map((b, index) => ({
		id: b.id,
		name: b.name,
		createdBy: b.createdBy
			? `${b.createdBy.firstName} ${b.createdBy.lastName}`
			: undefined,
		lastUpdated: b.updatedAt,
		isActive: true,
		order: index + 1,
		data: { audienceGroups: b.audienceGroups ?? [] }
	}));

	const draft = libraryBanners.map((b) => ({
		id: b.id,
		name: b.name,
		createdBy: b.createdBy
			? `${b.createdBy.firstName} ${b.createdBy.lastName}`
			: undefined,
		lastUpdated: b.updatedAt ?? b.createdAt,
		isActive: false,
		data: { audienceGroups: b.audienceGroups ?? [] }
	}));

	return [...live, ...draft];
}

const HomepageCarousel = () => {
	const navigate = useNavigate();
	const [activeBanners, setActiveBanners] = useState<Banner[]>([]);
	const [bannerLibrary, setBannerLibrary] = useState<Banner[]>([]);
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);
	const [filterGroupId, setFilterGroupId] = useState<
		number | "all" | "everyone"
	>("all");

	useActiveBanners({ onSuccess: setActiveBanners });
	useBannerLibrary({ onSuccess: setBannerLibrary });

	const { data: adminGroups = [] } = useAdminGroups();

	// If the currently-selected group disappears from adminGroups (deleted by
	// another admin while this page is open), reset the filter so the user
	// isn't stuck filtered to an invisible option.
	useEffect(() => {
		if (typeof filterGroupId !== "number") return;
		if (!adminGroups.some((g) => Number(g.id) === filterGroupId)) {
			setFilterGroupId("all");
		}
	}, [adminGroups, filterGroupId]);

	const { mutate: makeBannersInactive } = useMakeBannersInactive();
	const { mutate: moveCarouselBanner } = useMoveCarouselBanner();
	const { mutate: makeBannersActive } = useMakeBannersActive();
	const { mutate: deleteCarouselBanners, isLoading: isDeletingBanners } =
		useDeleteCarouselBanners();

	const items = useMemo(() => {
		const all = mapBannersToContentTableItems(activeBanners, bannerLibrary);
		if (filterGroupId === "all") return all;
		if (filterGroupId === "everyone")
			return all.filter((item) => item.data.audienceGroups.length === 0);
		return all.filter((item) =>
			item.data.audienceGroups.some((g) => g.id === filterGroupId)
		);
	}, [activeBanners, bannerLibrary, filterGroupId]);

	const handleMove = (item: ContentTableItem, direction: -1 | 1) => {
		moveCarouselBanner({ id: Number(item.id), offset: direction });
		setActiveBanners((prev) => {
			const index = prev.findIndex((b) => b.id === Number(item.id));
			if (index < 0) return prev;
			const newIndex = index + direction;
			if (newIndex < 0 || newIndex >= prev.length) return prev;
			const next = [...prev];
			[next[index], next[newIndex]] = [next[newIndex], next[index]];
			return next;
		});
	};

	const columns = useMemo<ContentTableColumn[]>(
		() => [
			{
				key: "createdBy",
				label: "Created by",
				render: (item) => item.createdBy ?? "—"
			},
			{
				key: "lastUpdated",
				label: "Last updated",
				// item.lastUpdated is already a formatted "DD MMM YYYY" string from
				// adaptCarouselBanner — don't re-parse.
				render: (item) => item.lastUpdated ?? "—"
			},
			{
				key: "audience",
				label: "Audience",
				render: (item) => {
					const groups =
						(item.data as { audienceGroups?: { id: number; name: string }[] })
							?.audienceGroups ?? [];
					return <AudienceCell groups={groups} />;
				}
			},
			{
				key: "order",
				label: "Order",
				width: "56px",
				render: (item) =>
					item.isActive && item.order !== null && item.order !== undefined ? String(item.order) : "—"
			}
		],
		[]
	);

	const selectedFilterLabel =
		filterGroupId === "all"
			? null
			: filterGroupId === "everyone"
				? "Everyone"
				: (adminGroups.find((g) => Number(g.id) === filterGroupId)?.name ??
					"group");
	const filterAnnouncement =
		filterGroupId === "all"
			? ""
			: `Showing ${items.length} banner${items.length === 1 ? "" : "s"} filtered by ${selectedFilterLabel}`;

	return (
		<Page>
			<p className="sr-only" aria-live="polite">
				{filterAnnouncement}
			</p>
			<ContentTable
				items={items}
				columns={columns}
				onRowClick={(item) =>
					navigate(
						`/vendor-sponsorship/homepage-carousel/banners/new-carousel-banner-form/${item.id}`
					)
				}
				onMakeLive={(item) => makeBannersActive([Number(item.id)])}
				onMakeDraft={(item) => makeBannersInactive([Number(item.id)])}
				onDelete={(item) => setItemToDelete(item)}
				canDelete={(item) => !item.isActive}
				onMoveUp={(item) => handleMove(item, -1)}
				onMoveDown={(item) => handleMove(item, 1)}
				createPath="/vendor-sponsorship/homepage-carousel/banners/new-carousel-banner-form"
				createLabel="Create Banner"
				searchRowExtra={
					<label className="flex items-center gap-2 text-sm">
						<span className="text-muted-foreground">Filter by group</span>
						<select
							value={filterGroupId}
							onChange={(e) => {
								const v = e.target.value;
								if (v === "all" || v === "everyone") setFilterGroupId(v);
								else setFilterGroupId(Number(v));
							}}
							className="rounded-lg border border-border bg-white px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
						>
							<option value="all">All groups</option>
							<option value="everyone">Everyone (no restrictions)</option>
							{adminGroups.map((g) => (
								<option key={g.id} value={Number(g.id)}>
									{g.name}
								</option>
							))}
						</select>
					</label>
				}
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete banner?"
				description="This action cannot be undone."
				onCancel={() => setItemToDelete(null)}
				onSubmit={() => {
					if (itemToDelete) {
						deleteCarouselBanners([Number(itemToDelete.id)]);
						setItemToDelete(null);
					}
				}}
				submitBtnProps={{ isLoading: isDeletingBanners }}
			/>
		</Page>
	);
};

export default HomepageCarousel;
